import { SearchableTree } from "@/shared-components/SearchableTree";
import { useEffect, useRef, useState } from "react";
import { IPanelScope, ISearchResult, ITreeItem } from "@/interfaces";
import { DEFAULT_FILTER_KEY, GENERIC_ERROR_MESSAGE } from "@/constants";
import { FilterTypes } from "@/enums";
import { FiltersService } from "@/services";
import { SelectableBehavior } from "../SelectableBehaivor/SelectableBehavior";
import { findNodeByIdAsync, createTreeFetchCallback } from "@/utils/common";
import styles from "./GenericSearchableTree.module.scss";
import { SEARCHABLE_TREE_WEB_COMPONENT_TESTID } from "@/constants/test-ids";
import { getTranslation } from "@/utils";

interface InitialSelectedItem {
  id: string;
  auto_field?: string;
  auto_field_id?: string;
  name: string;
}

interface GenericSearchableTreeProps {
  urls: {
    fetchRootTreeUrl: string;
    fetchTreeNode: string;
    search: string;
  };
  customClassName?: string;
  scope: IPanelScope;
  idsToRemove?: string[];
  excludeAutoFilters?: boolean;
  selectedValue: string | { id?: string } | undefined;
  disabled?: boolean;
  initialSelectedItem: InitialSelectedItem;
  onChange: (id: string) => void;
  notifyEvent?: { eventName: string; source: string };
  onFocus?: () => void;
  onBlur?: () => void;
  ariaLabelledBy?: string;
  displayName?: string;
}

export const GenericSearchableTree = ({
  scope,
  selectedValue,
  initialSelectedItem,
  customClassName,
  idsToRemove = [],
  excludeAutoFilters = false,
  urls,
  disabled = false,
  notifyEvent,
  onChange,
  onFocus,
  onBlur,
  ariaLabelledBy,
  displayName,
}: GenericSearchableTreeProps) => {
  const [treeItems, setTreeItems] = useState<ITreeItem[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [selectedItem, setSelectedItem] = useState<ITreeItem | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lastSearch, setLastSearch] = useState<string>();
  const refTreeFetched = useRef(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedNodeFetched, setSelectedNodeFetched] =
    useState<boolean>(false);
  useEffect(() => {
    // Reset the flag when dependencies change
    setSelectedNodeFetched(false);

    const fetchSelectedNode = async () => {
      if (urls === null) {
        setSelectedNodeFetched(true);
        return;
      }

      // startsWith("{{") is for angular support
      // the first time the angular render the component before he replace template values
      if (
        (!selectedValue || selectedValue?.toString().startsWith("{{")) &&
        !initialSelectedItem?.auto_field
      ) {
        setSelectedItem(undefined);
      } else {
        const getId = (): string => {
          if (typeof selectedValue === "object" && selectedValue?.id) {
            return selectedValue.id;
          }
          if (initialSelectedItem?.auto_field) return initialSelectedItem.id;
          // sport for-cross filter value
          if (
            typeof selectedValue === "string" &&
            selectedValue.includes("-[")
          ) {
            return selectedValue.split("-[")[0];
          }
          return selectedValue?.toString() || "";
        };

        const id = getId();

        if (id) {
          const item = await fetchTreeNode(id);
          await changeSelectedItem(item);
        }
      }
    };

    if (!refTreeFetched.current) {
      fetchSelectedNode().finally(() => {
        setSelectedNodeFetched(true);
      });
    } else {
      setSelectedNodeFetched(true);
    }
  }, [
    selectedValue,
    urls?.fetchRootTreeUrl,
    urls?.fetchTreeNode,
    urls?.search,
    refTreeFetched.current,
  ]);

  useEffect(() => {
    // Only run fetchRootTree after the selectedNode has been fetched
    // This prevents the race condition where selectedItem is undefined
    if (scope?.module && !disabled && selectedNodeFetched) {
      fetchRootTree();
    }
  }, [scope?.module, selectedValue, selectedNodeFetched]);

  const isExcludedItem = (item: ITreeItem): boolean =>
    idsToRemove.includes(item.id) ||
    (excludeAutoFilters &&
      (item.extra?.filterType === FilterTypes.AUTO_FILTER_ROOT ||
        item.extra?.filterType === FilterTypes.AUTO_FILTER));

  const fetchTreeNode = async (id: string): Promise<ITreeItem | undefined> => {
    if (!urls?.fetchTreeNode) return undefined;
    const parent = id.split("/")[0];
    let url = urls.fetchTreeNode
      .replace("[id]", parent)
      .replace("%5Bid%5D", parent)
      .replace(":id", parent);
    if (url.includes("custom_views") && !excludeAutoFilters) {
      url = url.concat("&auto_filter=all");
    }
    const res = await FiltersService.fetchFromServer(`${url}`);

    if (!res.node) return undefined;
    const node = res.node;

    node.children = node.children.filter((item) => !isExcludedItem(item));
    node.extra.hasChildren = !!node.children.length;

    return node;
  };

  const fetchSearchResult = async (
    criteria: string,
  ): Promise<ITreeItem[] | undefined> => {
    const criteriaInLowerCase = criteria.toLowerCase();
    const res = await FiltersService.fetchFromServer(
      `${urls.search}${urls.search.indexOf("?") === -1 ? "?" : "&"}query=${criteriaInLowerCase}`,
    );

    const items = removeExcludedItemsFromTree(res.items || []);

    return filterSearchItems(items, criteriaInLowerCase);
  };

  // Don't show parent if has selected requirement as single child and name not includes
  const filterSearchItems = (items: ITreeItem[], criteria: string) => {
    return items.filter((item) => {
      // Check if the item name matches the criteria
      const matchesCriteria = item.name.toLowerCase().includes(criteria);

      // Recursively filter children if they exist
      if (item.children && item.children.length > 0) {
        item.children = filterSearchItems(item.children, criteria);
      }

      // Keep the item if it matches the criteria or has non-empty children
      return matchesCriteria || (item.children && item.children.length > 0);
    });
  };

  const loadSelectedNodeFullTree = async (
    item: ITreeItem,
    tree: ITreeItem[],
  ) => {
    const ancestries = item.serverObject.ancestry?.split("/") || [];
    if (ancestries.length) {
      for (let i = 0; i < ancestries.length; i++) {
        const parentNode = await fetchTreeNode(ancestries[i]);
        if (parentNode && tree) {
          const fetchCallback = createTreeFetchCallback(scope);
          const node = await findNodeByIdAsync(
            tree,
            parentNode.id,
            fetchCallback,
          );
          if (node) {
            node.children = parentNode.children;
          }
        }
      }
    }
  };

  const removeExcludedItemsFromTree = (items: ITreeItem[]): ITreeItem[] => {
    const filterItems = items?.filter((item) => !isExcludedItem(item));
    filterItems?.map((node) => {
      const originalChildrenCount = node.children.length || 0;
      node.children = node.children.filter((item) => !isExcludedItem(item));
      node.extra.hasChildren =
        (originalChildrenCount === 0 && node.serverObject.has_children) ||
        (originalChildrenCount > 0 && node.children.length > 0);
      removeExcludedItemsFromTree(node.children);
    });
    return filterItems;
  };

  const findFilterByPath = async (
    items: ITreeItem[],
    autoFields: string[],
    baseItem: ITreeItem,
  ): Promise<ITreeItem | undefined> => {
    const baseId = baseItem.id;
    const autoPath = baseItem.serverObject?.auto || [];

    if (autoPath.length === 0) return undefined;
    let expectedPath = baseId;
    expectedPath += `/${autoPath[0]}`;
    if (initialSelectedItem?.auto_field_id) {
      expectedPath += `/${initialSelectedItem.auto_field_id}`;
    }
    for (let i = 0; i < autoFields.length; i++) {
      expectedPath += `/${autoFields[i]}`;
      if (i < autoFields.length - 1 && autoPath[i + 1]) {
        expectedPath += `/${autoPath[i + 1]}`;
      }
    }

    const fetchCallback = createTreeFetchCallback(scope);
    const result = await findNodeByIdAsync(items, expectedPath, fetchCallback);

    return result;
  };

  const changeSelectedItem = async (item: ITreeItem | undefined) => {
    if (!initialSelectedItem?.auto_field) {
      setSelectedItem(item);
      return;
    }

    // This code is for report only
    if (item && initialSelectedItem.auto_field) {
      const autoFields = initialSelectedItem.auto_field.split(";");

      const _selectedItem = await findFilterByPath(
        item.children,
        autoFields,
        item,
      );

      _selectedItem && setSelectedItem(_selectedItem);
    }
  };

  const fetchRootTree = async () => {
    try {
      setIsLoading(true);

      const { items } = await FiltersService.fetchFromServer(
        urls.fetchRootTreeUrl,
      );

      const filteredItems = items?.length
        ? removeExcludedItemsFromTree(items)
        : [];

      if (selectedItem && items?.length) {
        await loadSelectedNodeFullTree(selectedItem, items);
      }

      setTreeItems(filteredItems || []);
    } catch (err) {
      console.error("fetchFolders->Failure fetch folders", err);
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNodeExpand = async (node: ITreeItem) => {
    if (
      !node.extra.hasChildren ||
      node.children.length ||
      (node.extra.hasChildren && node.children.length)
    )
      return;

    node.extra.isLoading = true;
    node.extra.error = null;

    try {
      const item = node.id.includes("/")
        ? await FiltersService.fetchAutoFilterChildren(scope, node)
        : await fetchTreeNode(node.id);

      if (item) {
        node.extra.isLoading = false;
        if (item.children) {
          node.children = [...item.children];
        }
      }
    } catch (e) {
      console.error(`FolderSearchableTree=>handleNodeExpand`, e);
      node.extra.error = GENERIC_ERROR_MESSAGE;
    } finally {
      node.extra.isLoading = false;
    }
  };

  const handleSearch = async (criteria: string) => {
    try {
      if (lastSearch === criteria) return;
      setIsLoading(true);
      const items = await fetchSearchResult(criteria);
      setLastSearch(criteria);
      //Todo: remove this when we have a better solution
      return { filteredTree: items } as ISearchResult;
    } catch (err) {
      console.error("handleSearch->Failure search folders", err);
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelected = (node?: ITreeItem): void => {
    handleSussestionDivOpen(false);
    setSelectedItem(node);

    if (typeof onChange === "function") {
      onChange(node?.serverObject || { id: DEFAULT_FILTER_KEY });
    }

    if (notifyEvent) {
      //Support shadowDom webComponent
      const event = new CustomEvent(notifyEvent.eventName, {
        detail: {
          node: node?.serverObject || { id: DEFAULT_FILTER_KEY },
          instanceId: scope.instanceId,
        },
      });
      document.querySelector(notifyEvent.source)?.dispatchEvent(event);
    }
  };

  const getDisplayName = () => {
    return displayName || selectedItem?.name;
  };

  const handleSussestionDivOpen = async (isOpen: boolean) => {
    if (isOpen && !refTreeFetched.current) {
      await fetchRootTree();
      refTreeFetched.current = true;
    }
    setIsOpen(isOpen);
    if (isOpen) {
      onFocus?.();
    } else {
      onBlur?.();
    }
  };

  return !scope?.module ? (
    <></>
  ) : (
    <div
      data-testid={SEARCHABLE_TREE_WEB_COMPONENT_TESTID}
      className={`${styles.GenericSearchableTree} ${customClassName || ""}`}
    >
      <SelectableBehavior
        disabled={disabled}
        onClear={() => handleSelected(undefined)}
        isOpen={isOpen}
        handleSussestionDivOpen={handleSussestionDivOpen}
        value={getDisplayName()}
        placeholder={getTranslation("generic.list.placeholder", "Select value")}
        ariaLabelledBy={ariaLabelledBy}
      >
        <SearchableTree
          isLoading={isLoading}
          items={treeItems}
          addSelectAll={false}
          error={error}
          selectedItem={selectedItem}
          scope={scope}
          handleSelected={handleSelected}
          handleNodeExpand={handleNodeExpand}
          handleSearch={handleSearch}
        />
      </SelectableBehavior>
    </div>
  );
};
